import React, { useEffect, useRef, useState } from 'react';
import { registerFromInvite, passkeysSupported, explain, isCancel } from './authApi';
import AuthShell, { FaceIdIcon, Notices, Spinner, primaryBtn, type NoticeData } from './AuthShell';

/**
 * /zaproszenie#t=<token>. The token lives in the URL FRAGMENT (never sent to a server
 * or logged by proxies); we strip it from the address bar immediately and keep it in memory.
 */
const STEPS = ['Dotknij przycisk', 'Potwierdź Face ID / Touch ID', 'Gotowe'];

const InvitePage: React.FC = () => {
  const token = useRef('');
  const busyRef = useRef(false);
  const [has, setHas] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<NoticeData | null>(null);
  const [done, setDone] = useState(false);
  const supported = passkeysSupported();

  useEffect(() => {
    const t = new URLSearchParams(window.location.hash.slice(1)).get('t') || '';
    // StrictMode (dev) runs effects twice: the 2nd run sees an already-stripped URL, so never overwrite a token we hold.
    if (t) { token.current = t; window.history.replaceState(null, '', window.location.pathname); }
    setHas(!!token.current);
  }, []);

  const go = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setMsg(null);
    try {
      await registerFromInvite(token.current, name.trim());
      token.current = '';
      setDone(true);
      setTimeout(() => window.location.replace('/'), 1500);
    } catch (e) {
      setMsg(isCancel(e)
        ? { kind: 'info', text: 'Anulowano. Gdy będziesz gotowy, dotknij przycisku jeszcze raz — link nadal działa.' }
        : { kind: 'error', text: explain(e) });
      busyRef.current = false; setBusy(false);
    }
  };

  const current = done ? 2 : busy ? 1 : 0;
  return (
    <AuthShell title="Dołącz do WISC-V" subtitle={done ? undefined : 'Zapisz to urządzenie jako klucz dostępu. Zajmie to kilka sekund.'}>
      {!has && !done ? (
        <Notices items={[{ kind: 'error', text: 'Brak tokenu w linku. Otwórz link zaproszenia w całości albo poproś o nowy (ważny 24 godziny, jednorazowy).' }]} />
      ) : (
        <>
          <ol className="space-y-3" aria-label="Kroki">
            {STEPS.map((s, i) => {
              const state = i < current || done ? 'done' : i === current ? 'active' : 'todo';
              return (
                <li key={s} aria-current={state === 'active' ? 'step' : undefined} className="flex items-center gap-3">
                  <span className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-sm font-semibold ${
                    state === 'todo' ? 'border border-brd text-txt-tertiary' : 'bg-accent-strong text-txt-onAccent'}`}>
                    {state === 'done' ? '✓' : i + 1}
                  </span>
                  <span className={`text-sm ${state === 'todo' ? 'text-txt-tertiary' : 'font-medium text-txt-primary'}`}>{s}</span>
                </li>
              );
            })}
          </ol>
          {done ? (
            <div className="mt-6"><Notices items={[{ kind: 'success', text: 'Gotowe! Od teraz logujesz się Face ID / Touch ID. Przekierowuję…' }]} /></div>
          ) : (
            <div className="mt-6 space-y-3">
              <Notices items={[!supported && { kind: 'error', text: 'Ta przeglądarka nie obsługuje passkeyów. Otwórz link w aktualnej przeglądarce Safari lub Chrome na zaktualizowanym systemie.' }, msg]} />
              <div>
                <label className="block text-sm text-txt-secondary" htmlFor="pk-name">Nazwa urządzenia (opcjonalnie)</label>
                <input id="pk-name" value={name} maxLength={40} onChange={e => setName(e.target.value)} placeholder="np. iPhone"
                  autoComplete="off" disabled={busy}
                  className="mt-1 w-full min-h-[48px] rounded-xl border border-brd bg-surface-page px-3 text-base text-txt-primary placeholder:text-txt-tertiary" />
              </div>
              <button type="button" onClick={go} disabled={busy || !supported} aria-busy={busy} className={primaryBtn}>
                {busy ? <Spinner /> : <FaceIdIcon />}
                <span>{busy ? 'Potwierdź na urządzeniu…' : 'Dodaj passkey'}</span>
              </button>
            </div>
          )}
        </>
      )}
    </AuthShell>
  );
};

export default InvitePage;
